import React, { useMemo, useState, useCallback, useRef } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { HEX } from "../../lib/colorSystem";

// Types
interface SessionTimelineRow {
  setter_id: string;
  work_date: string; // "2026-02-07"
  session_start: string; // ISO timestamp (UTC)
  session_end: string; // ISO timestamp (UTC)
  message_count: number;
}

interface SessionClockChartProps {
  sessions: SessionTimelineRow[];
  dates: string[]; // all dates in range, most recent first (for ring ordering)
  timezone: string; // IANA timezone like "Asia/Manila"
  size?: number; // default 350
  /** Called when user clicks a session arc — opens messages for that session */
  onSessionClick?: (sessionStart: string, sessionEnd: string, label: string) => void;
  /** Compact mode: hide hints, legend, and show only 4 hour labels (12a, 6a, 12p, 6p) */
  compact?: boolean;
}

interface RingDimensions {
  date: string;
  innerRadius: number;
  outerRadius: number;
  index: number;
}

interface ArcData {
  path: string;
  tooltipContent: string;
  ringIndex: number;
  /** Original session start (ISO) — for click-through to messages */
  sessionStart: string;
  /** Original session end (ISO) — for click-through to messages */
  sessionEnd: string;
  /** Work date string e.g. "2026-02-10" */
  workDate: string;
}

// Helper: Get minute of day (0-1439) for a timestamp in a given timezone
function getMinuteOfDay(isoStr: string, tz: string): number {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date(isoStr));
  const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
  const minute = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
  return hour * 60 + minute;
}

// Helper: Get calendar date in timezone
function getCalendarDate(isoStr: string, tz: string): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date(isoStr));
}

// Helper: SVG arc path
function describeArc(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number
): string {
  const startRad = ((startAngle - 90) * Math.PI) / 180;
  const endRad = ((endAngle - 90) * Math.PI) / 180;

  const x1 = cx + innerR * Math.cos(startRad);
  const y1 = cy + innerR * Math.sin(startRad);
  const x2 = cx + innerR * Math.cos(endRad);
  const y2 = cy + innerR * Math.sin(endRad);
  const x3 = cx + outerR * Math.cos(endRad);
  const y3 = cy + outerR * Math.sin(endRad);
  const x4 = cx + outerR * Math.cos(startRad);
  const y4 = cy + outerR * Math.sin(startRad);

  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${x1} ${y1}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

// Helper: Format time as "9:15 AM"
function formatClockTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

// Helper: Format duration as "1h 27m" or "45m"
function formatDuration(startMinute: number, endMinute: number): string {
  const totalMinutes = endMinute - startMinute;
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

// Helper: Format date as "Fri, Feb 7"
function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00Z");
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return formatter.format(date);
}

// Component
export const SessionClockChart: React.FC<SessionClockChartProps> = ({
  sessions,
  dates,
  timezone,
  size = 350,
  onSessionClick,
  compact = false,
}) => {
  // Constants
  const center = size / 2;
  const outerRadius = size / 2 - 30;
  const innerRadius = size * 0.15;
  const ringGap = 2;

  // Compute ring dimensions per date
  const rings = useMemo<RingDimensions[]>(() => {
    if (dates.length === 0) return [];
    const ringWidth = (outerRadius - innerRadius) / dates.length;
    return dates.map((date, i) => ({
      date,
      innerRadius: outerRadius - (i + 1) * ringWidth + ringGap,
      outerRadius: outerRadius - i * ringWidth - ringGap,
      index: i,
    }));
  }, [dates, outerRadius, innerRadius, ringGap]);

  // Compute arc paths
  const arcs = useMemo<ArcData[]>(() => {
    if (rings.length === 0) return [];

    const arcList: ArcData[] = [];

    sessions.forEach((session) => {
      // Convert timestamps to minute-of-day in timezone
      const startDate = getCalendarDate(session.session_start, timezone);
      const endDate = getCalendarDate(session.session_end, timezone);

      const startMinute = getMinuteOfDay(session.session_start, timezone);
      const endMinute = getMinuteOfDay(session.session_end, timezone);

      // Find which ring this session belongs to
      const ring = rings.find((r) => r.date === session.work_date);
      if (!ring) return;

      // Handle sessions spanning midnight
      if (startDate !== endDate) {
        // Part 1: From start to midnight (1440 minutes)
        const startAngle = (startMinute / 1440) * 360;
        const endAngle = 360;
        const path1 = describeArc(
          center,
          center,
          ring.innerRadius,
          ring.outerRadius,
          startAngle,
          endAngle
        );
        const tooltip1 = `${formatClockTime(startMinute)} – 11:59 PM (${formatDuration(
          startMinute,
          1440
        )})`;
        arcList.push({
          path: path1,
          tooltipContent: tooltip1,
          ringIndex: ring.index,
          sessionStart: session.session_start,
          sessionEnd: session.session_end,
          workDate: session.work_date,
        });

        // Part 2: From midnight to end (if end date has a ring)
        const endRing = rings.find((r) => r.date === endDate);
        if (endRing) {
          const startAngle2 = 0;
          const endAngle2 = Math.max((endMinute / 1440) * 360, (5 / 1440) * 360);
          const path2 = describeArc(
            center,
            center,
            endRing.innerRadius,
            endRing.outerRadius,
            startAngle2,
            endAngle2
          );
          const tooltip2 = `12:00 AM – ${formatClockTime(endMinute)} (${formatDuration(
            0,
            endMinute
          )})`;
          arcList.push({
            path: path2,
            tooltipContent: tooltip2,
            ringIndex: endRing.index,
            sessionStart: session.session_start,
            sessionEnd: session.session_end,
            workDate: session.work_date,
          });
        }
      } else {
        // Normal session within same day
        let duration = endMinute - startMinute;
        const minArcMinutes = 5;

        // Apply minimum arc width
        if (duration < minArcMinutes) {
          duration = minArcMinutes;
        }

        const startAngle = (startMinute / 1440) * 360;
        const endAngle = ((startMinute + duration) / 1440) * 360;

        const path = describeArc(
          center,
          center,
          ring.innerRadius,
          ring.outerRadius,
          startAngle,
          endAngle
        );

        let tooltipContent: string;
        if (session.message_count === 1 || endMinute === startMinute) {
          tooltipContent = `${formatClockTime(startMinute)} (${session.message_count} msg${
            session.message_count !== 1 ? "s" : ""
          })`;
        } else {
          tooltipContent = `${formatClockTime(startMinute)} – ${formatClockTime(
            endMinute
          )} (${formatDuration(startMinute, endMinute)})`;
        }

        arcList.push({
          path,
          tooltipContent,
          ringIndex: ring.index,
          sessionStart: session.session_start,
          sessionEnd: session.session_end,
          workDate: session.work_date,
        });
      }
    });

    return arcList;
  }, [sessions, rings, timezone, center]);

  // Hour positions for labels and grid lines
  // In compact mode: only 4 labels (12a, 6a, 12p, 6p) — cleaner appearance
  const hourPositions = useMemo(() => {
    const hours = compact ? [0, 6, 12, 18] : [0, 3, 6, 9, 12, 15, 18, 21];
    const labelRadius = outerRadius + (compact ? 12 : 18);

    return hours.map((h) => {
      const angle = ((h / 24) * 360 - 90) * (Math.PI / 180);
      const label =
        h === 0
          ? "12a"
          : h === 6
            ? "6a"
            : h === 12
              ? "12p"
              : h === 18
                ? "6p"
                : h < 12
                  ? `${h}a`
                  : `${h - 12}p`;
      return {
        hour: h,
        angle,
        label,
        x: center + labelRadius * Math.cos(angle),
        y: center + labelRadius * Math.sin(angle),
      };
    });
  }, [center, outerRadius, compact]);

  // ─── Zoom & Pan state ──────────────────────────────────────────────────────
  const [vb, setVb] = useState({ x: 0, y: 0, w: size, h: size });
  const svgRef = useRef<SVGSVGElement>(null);
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0, vbX: 0, vbY: 0 });

  const MIN_ZOOM = size; // 1x (full chart)
  const MAX_ZOOM = size / 5; // 5x zoom

  const isZoomed = vb.w < size * 0.99;

  // Convert screen coords to SVG viewBox coords
  const screenToSvg = useCallback(
    (screenX: number, screenY: number) => {
      if (!svgRef.current) return { x: 0, y: 0 };
      const rect = svgRef.current.getBoundingClientRect();
      return {
        x: vb.x + ((screenX - rect.left) / rect.width) * vb.w,
        y: vb.y + ((screenY - rect.top) / rect.height) * vb.h,
      };
    },
    [vb]
  );

  // Zoom toward cursor on scroll wheel
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const zoomFactor = e.deltaY > 0 ? 1.15 : 0.85; // scroll down = zoom out
      const cursor = screenToSvg(e.clientX, e.clientY);

      setVb((prev) => {
        const newW = Math.min(MIN_ZOOM, Math.max(MAX_ZOOM, prev.w * zoomFactor));
        const newH = newW; // keep square
        // Zoom toward cursor: adjust origin so cursor stays in same screen position
        const ratio = newW / prev.w;
        const newX = cursor.x - (cursor.x - prev.x) * ratio;
        const newY = cursor.y - (cursor.y - prev.y) * ratio;
        return { x: newX, y: newY, w: newW, h: newH };
      });
    },
    [screenToSvg, MIN_ZOOM, MAX_ZOOM]
  );

  // Drag-to-pan when zoomed in
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isZoomed) return;
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY, vbX: vb.x, vbY: vb.y };
      e.preventDefault();
    },
    [isZoomed, vb.x, vb.y]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning.current || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const dx = ((e.clientX - panStart.current.x) / rect.width) * vb.w;
      const dy = ((e.clientY - panStart.current.y) / rect.height) * vb.h;
      setVb((prev) => ({
        ...prev,
        x: panStart.current.vbX - dx,
        y: panStart.current.vbY - dy,
      }));
    },
    [vb.w, vb.h]
  );

  const handleMouseUp = useCallback(() => {
    isPanning.current = false;
  }, []);

  // Double-click to reset zoom
  const handleDoubleClick = useCallback(() => {
    setVb({ x: 0, y: 0, w: size, h: size });
  }, [size]);

  // No sessions case
  if (sessions.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-glass-muted text-sm"
        style={{ width: size, height: size }}
      >
        No session data
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Zoom hint — hidden in compact mode */}
      {!compact && (
        <div className="token-10 text-glass-muted mb-1">
          {isZoomed ? "Drag to pan · Double-click to reset" : "Scroll to zoom in"}
        </div>
      )}

      <svg
        ref={svgRef}
        className="anim-radial-in"
        width={size}
        height={size}
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: isZoomed ? "grab" : "default" }}
      >
        {/* Hour grid lines */}
        {hourPositions.map((pos) => {
          const outerX = center + outerRadius * Math.cos(pos.angle);
          const outerY = center + outerRadius * Math.sin(pos.angle);
          return (
            <line
              key={`grid-${pos.hour}`}
              x1={center}
              y1={center}
              x2={outerX}
              y2={outerY}
              stroke={HEX.gray200}
              strokeWidth={0.5}
            />
          );
        })}

        {/* Ring backgrounds */}
        {rings.map((ring) => {
          const midRadius = (ring.innerRadius + ring.outerRadius) / 2;
          return (
            <circle
              key={`ring-bg-${ring.date}`}
              cx={center}
              cy={center}
              r={midRadius}
              fill="none"
              stroke="rgba(0,0,0,0.04)"
              strokeWidth={ring.outerRadius - ring.innerRadius - ringGap}
            />
          );
        })}

        {/* Session arcs */}
        {arcs.map((arc, i) => (
          <path
            key={`arc-${i}`}
            data-testid="session-arc"
            d={arc.path}
            fill={HEX.accent}
            className="cursor-pointer"
            style={{ opacity: 0.9, transition: "opacity 0.15s" }}
            data-tooltip-id="session-clock-tooltip"
            data-tooltip-content={arc.tooltipContent}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
            onClick={() => {
              if (onSessionClick) {
                onSessionClick(
                  arc.sessionStart,
                  arc.sessionEnd,
                  `${formatDateLabel(arc.workDate)} — ${arc.tooltipContent}`
                );
              }
            }}
          />
        ))}

        {/* Hour labels */}
        {hourPositions.map((pos) => (
          <text
            key={`label-${pos.hour}`}
            x={pos.x}
            y={pos.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-current text-glass-muted token-11"
          >
            {pos.label}
          </text>
        ))}
      </svg>

      {/* Day legend — hidden in compact mode */}
      {!compact && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-3 token-11 text-glass-muted">
          {dates.map((date, i) => {
            const opacity = 1 - i * 0.15; // Most recent = full opacity
            return (
              <div key={date} className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: HEX.accent, opacity: Math.max(opacity, 0.3) }}
                />
                <span>{formatDateLabel(date)}</span>
              </div>
            );
          })}
        </div>
      )}

      <ReactTooltip
        id="session-clock-tooltip"
        className="token-13"
        style={{
          backgroundColor: HEX.accentHover,
          borderRadius: "6px",
          padding: "6px 10px",
        }}
      />
    </div>
  );
};
